/**
 * Simple event emitter for announcements synchronization across components
 */
type AnnouncementListener = (announcement: unknown) => void;

/**
 * Event emitter to manage announcement listeners
 */
class AnnouncementEventEmitter {
  private listeners: Set<AnnouncementListener> = new Set();

  constructor() {
    // Listen for the custom event dispatched from the SocketService
    window.addEventListener('systemAnnouncement', (event: Event) => {
      const customEvent = event as CustomEvent;
      console.debug(
        '[AnnouncementEventEmitter] Received systemAnnouncement from window:',
        customEvent.detail,
      );
      this.emit(customEvent.detail);
    });
  }

  /**
   * Register a new listener
   *
   * @param listener - The listener to add
   */
  on(listener: AnnouncementListener): void {
    console.debug(
      '[AnnouncementEventEmitter] Registering listener, total listeners:',
      this.listeners.size + 1,
    );
    this.listeners.add(listener);
  }

  /**
   * Unregister an existing listener
   *
   * @param listener - The listener to remove
   */
  off(listener: AnnouncementListener): void {
    console.debug(
      '[AnnouncementEventEmitter] Unregistering listener, total listeners:',
      this.listeners.size - 1,
    );
    this.listeners.delete(listener);
  }

  /**
   * Emit an announcement to all registered listeners
   *
   * @param announcement - The announcement to emit
   */
  emit(announcement: unknown): void {
    console.debug('[AnnouncementEventEmitter] Emitting event to', this.listeners.size, 'listeners');
    if (this.listeners.size === 0) {
      console.warn('[AnnouncementEventEmitter] WARNING: No listeners registered!');
    }
    this.listeners.forEach((listener) => listener(announcement));
  }
}

export const announcementEventEmitter = new AnnouncementEventEmitter();
